import { Rating, Button } from '@mui/material';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import DownloadIcon from '@mui/icons-material/Download';
import { downloadFileWithName } from '../../utilities/functions/DownloadUtil';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { useParams } from 'react-router-dom';
import { getRequestDetail, sendQualificationAndRating } from '../../api/ServiceRequestedDetails';
import { cacheConfig } from '../../cacheConfig';
import CenterLoading from '../../components/CenterLoading/CenterLoading';
import DeskNotification from '../../components/DeskNotification/DeskNotification';
import FormModal from '../../components/FormModal/FormModal';
import TextField from '../../components/TextField/TextField';
import { ROLE_TYPE } from '../../constants/constants';
import { HideGlobalLoading, ShowGlobalLoading, UpdateAppSubHeaderTitle } from '../../redux/actions/UiActions';
import COLORS from '../../theme/Colors';
import FONTS_FAMILY from '../../theme/FontsFamily';
import { FormRatingSchema, MenuOptions } from './ServiceRequestedDetailsConstants';
import ActionsRequired from './subViews/actionsRequired/ActionsRequired';
import ComplaintsAndClaims from './subViews/complaintsAndClaims/ComplaintsAndClaims';
import Details from './subViews/details/Details';
import Messages from './subViews/messages/Messages';
import Payment from './subViews/payments/Payments';

function ServiceRequestedDetails() {
    const history = useHistory();
    const location = useLocation();

    // Obtener los query params
    const queryParams = new URLSearchParams(location.search);
    const docIdentification = queryParams.get('doc_identification');
    const userRolCompany = queryParams.get('user_rol_company');
    const statusID = queryParams.get('status_id');
    const companyID = queryParams.get('company_id');
    const companyName = queryParams.get('company_name');

    const { requestID } = useParams();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    const [activeMenu, setActiveMenu] = useState(0);
    const [ratingModalIsOpen, setRatingModalIsOpen] = useState(false);
    const [rating, setRating] = useState(3);
    const { rolBase, citizenID } = useSelector((state) => state.authReducer);

    const [selectedActionRequired, setSelectedActionRequired] = useState();
    const actionRequiredRef = useRef(null);

    const [availableMenus, setAvailableMenus] = useState({
        details: true,
        claims: true,
        payment: false,
        actionRequired: false,
        messages: false
    });

    const formik = useFormik({
        initialValues: {
            comment: ''
        },
        validationSchema: FormRatingSchema,
        onSubmit: (values) => {
            handleSendRating(values);
        }
    });

    const { data: serviceRequestedDetail, isLoading } = useQuery(
        ['serviceRequestedDetail', requestID],
        async () => {
            try {
                dispatch(ShowGlobalLoading('Cargando'));
                const response = await getRequestDetail(
                    requestID,
                    rolBase == ROLE_TYPE.COMPANY || userRolCompany == ROLE_TYPE.REPRESENTATIVE ? docIdentification : citizenID
                );
                if (response?.request === undefined) {
                    // history.push("/public");
                    throw Error;
                }
                dispatch(HideGlobalLoading());
                return response;
            } catch (error) {
                // history.push("/public");
                dispatch(HideGlobalLoading());
            }
        },
        {
            staleTime: cacheConfig.staleTimeForRequestedServiceDetail
        }
    );

    const mutation = useMutation(sendQualificationAndRating);

    const handleChangeMenu = (menuID) => {
        setActiveMenu(menuID);
        window.location.hash = '';
        setSelectedActionRequired(null);
    };

    const handleRatingModalVisibility = () => {
        setRatingModalIsOpen(!ratingModalIsOpen);
    };

    const handleSendRating = (formData) => {
        dispatch(ShowGlobalLoading('Cargando'));
        mutation.mutate(
            {
                request_id: requestID,
                rating: rating,
                comment: formData.comment
            },
            {
                onSettled: () => {
                    dispatch(HideGlobalLoading());
                },
                onSuccess: () => {
                    enqueueSnackbar('Calificación enviada con exito', { variant: 'success' });
                    queryClient.invalidateQueries(['serviceComments', serviceRequestedDetail.request.service.id + '']);
                    queryClient.invalidateQueries(['serviceRequestedDetail', requestID]);
                    handleRatingModalVisibility();
                },
                onError: () => {
                    enqueueSnackbar('Ha ocurrido un error', { variant: 'error' });
                }
            }
        );
    };

    const handleCompleteActionRequired = (actionRequired) => {
        setSelectedActionRequired(actionRequired);

        if (docIdentification != citizenID) {
            setActiveMenu(MenuOptions.details);
            return;
        }
        switch (actionRequired?.action_id) {
            case 1:
                setActiveMenu(MenuOptions.actionRequired);
                break;
            case 3:
                setActiveMenu(MenuOptions.messages);
                break;
            case 5:
                setActiveMenu(MenuOptions.payment);
                break;
            case 7:
                setActiveMenu(MenuOptions.payment);
                break;
            default:
                setActiveMenu(MenuOptions.details);
                break;
        }
        actionRequiredRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const finishActionRequired = () => {
        setActiveMenu(MenuOptions.details);
        setSelectedActionRequired(null);
    };

    const setAvailableSubViews = (requestActions = []) => {
        let localAvailableMenus = {
            details: true,
            claims: true,
            payment: false,
            actionRequired: false,
            messages: false
        };
        //show payment menu
        if (
            (serviceRequestedDetail.request.service.external_pay == 1 && serviceRequestedDetail.request?.payment?.payment_amount > 0) ||
            (serviceRequestedDetail.request.service.sirit_code != null && serviceRequestedDetail.request?.payment?.payment_amount > 0) ||
            serviceRequestedDetail.priceRequest?.length > 0
        ) {
            localAvailableMenus = { ...localAvailableMenus, payment: true };
        }
        //show messages menu
        if (serviceRequestedDetail.request.comments.length > 0 || requestActions.some((item) => item?.action_id === 3)) {
            localAvailableMenus = { ...localAvailableMenus, messages: true };
        }
        setAvailableMenus(localAvailableMenus);
    };

    useLayoutEffect(() => {
        if (serviceRequestedDetail !== undefined) {
            const { request, requiredActions
            } = serviceRequestedDetail || {};

            const paymentRequiredAction = requiredActions.find((item) => item?.action_id === 7 || item?.action_id === 5);
            const { approval_number } = request || {};

            if(approval_number && paymentRequiredAction){
                const payment = request?.payment;

                if(payment){
                    const paymentDate = new Date(payment.succesfullyPayment_date);
                    const actionRequiredDate = new Date(paymentRequiredAction.created_at);

                    if (
                        !isNaN(paymentDate.getTime()) &&
                        !isNaN(actionRequiredDate.getTime()) &&
                        paymentDate > actionRequiredDate
                    ) {
                        const url = `/app/validatePayment/${request.id}?requiredActionID=${paymentRequiredAction.id}&numAprobacion=${approval_number}&idAutorizacionPortal=${request.idAutorizacionPortal}`;
                        history.push(url);    
                    }

                }
            }
            dispatch(UpdateAppSubHeaderTitle(serviceRequestedDetail.request.service.name));
            setAvailableSubViews(serviceRequestedDetail.requiredActions);
            if (statusID != 10 && statusID != 8 && statusID != 7)
                serviceRequestedDetail?.requiredActions?.map((item) => handleCompleteActionRequired(item));
        }
    }, [serviceRequestedDetail]);

    // Build tab items dynamically
    const tabItems = [];
    tabItems.push({ id: MenuOptions.details, label: 'Detalles' });
    if (availableMenus.payment && docIdentification == citizenID) {
        tabItems.push({ id: MenuOptions.payment, label: 'Pagos' });
    }
    if (availableMenus.messages) {
        tabItems.push({ id: MenuOptions.messages, label: 'Mensajes' });
    }
    if (statusID != 10) {
        tabItems.push({ id: MenuOptions.claims, label: 'Quejas y Reclamaciones' });
    }

    if (isLoading) return <CenterLoading />;

    const isCompletedStatus = statusID == 7 || 
        String(serviceRequestedDetail?.request?.status?.name || '').toLowerCase().includes('completad') ||
        String(serviceRequestedDetail?.request?.status?.name || '').toLowerCase().includes('aprobad') ||
        String(serviceRequestedDetail?.request?.status?.name || '').toLowerCase().includes('finalizad');

    const resolutionHeaderDoc = (() => {
        if (!serviceRequestedDetail?.request || !isCompletedStatus) return null;
        const req = serviceRequestedDetail.request;
        const instDoc = req.request_document?.find(d => 
            d.origin_doc === 1 || 
            String(d.name || '').toLowerCase().includes('resolu') ||
            String(d.name || '').toLowerCase().includes('licencia') ||
            String(d.name || '').toLowerCase().includes('certific')
        );
        const url = instDoc?.url || req.resolution_url || req.url_resolution || req.resolution_file || req.resolution_document;
        const name = instDoc?.name 
            ? `${instDoc.name}.${instDoc.extension || 'pdf'}` 
            : `Resolución_${req.approval_number || req.code || req.id}.pdf`;
        return { url: url || '#', name };
    })();

    return (
        <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            width: '100%',
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '24px 16px'
        }}>
            {/* Header: Service Name + Status Badge */}
            {serviceRequestedDetail && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '12px',
                    marginBottom: '24px',
                    padding: '20px 28px',
                    background: '#eef2ff',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0'
                }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                        <p style={{ 
                            margin: 0, 
                            fontSize: '12px', 
                            color: '#94a3b8',
                            fontFamily: FONTS_FAMILY.bold.body,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            marginBottom: '4px'
                        }}>
                            Solicitud {serviceRequestedDetail.request?.code}
                        </p>
                        <h2 style={{ 
                            margin: 0, 
                            fontSize: '22px', 
                            fontWeight: 700,
                            color: COLORS.secondary,
                            fontFamily: FONTS_FAMILY.bold.body,
                            lineHeight: 1.3
                        }}>
                            {serviceRequestedDetail.request?.service?.name}
                        </h2>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        {isCompletedStatus && (
                            <>
                                <Button
                                    variant="contained"
                                    startIcon={<DownloadIcon />}
                                    onClick={() => downloadFileWithName(resolutionHeaderDoc?.url, resolutionHeaderDoc?.name)}
                                    sx={{
                                        backgroundColor: '#0063BC',
                                        color: '#FFFFFF',
                                        fontWeight: 700,
                                        fontSize: '13px',
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        px: 2.5,
                                        py: 1,
                                        boxShadow: '0 2px 8px rgba(0, 99, 188, 0.25)',
                                        fontFamily: FONTS_FAMILY.bold.body,
                                        '&:hover': {
                                            backgroundColor: '#004f98',
                                            boxShadow: '0 4px 12px rgba(0, 99, 188, 0.35)',
                                        }
                                    }}
                                >
                                    Descargar Resolución
                                </Button>

                                <Button
                                    variant="outlined"
                                    startIcon={<StarRoundedIcon style={{ color: '#F59E0B' }} />}
                                    onClick={handleRatingModalVisibility}
                                    sx={{
                                        backgroundColor: '#FEF3C7',
                                        color: '#B45309',
                                        borderColor: '#FCD34D',
                                        fontWeight: 700,
                                        fontSize: '13px',
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        px: 2.5,
                                        py: 1,
                                        fontFamily: FONTS_FAMILY.bold.body,
                                        '&:hover': {
                                            backgroundColor: '#FDE68A',
                                            borderColor: '#F59E0B',
                                        }
                                    }}
                                >
                                    Calificar Servicio
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Payment Notification Card */}
            {serviceRequestedDetail?.request?.payment && (() => {
                const paymentInfo = serviceRequestedDetail.request.payment;
                const amountNum = parseFloat(paymentInfo.payment_amount || 0);
                if (amountNum === 0 || paymentInfo.payment_amount === '0.00' || paymentInfo.payment_amount === '0') {
                    return null;
                }
                const isPaid = paymentInfo.payment_status === 'PAGADO' || paymentInfo.payment_status === 'COMPLETADO';
                return (
                    <div style={{
                        marginBottom: '20px',
                        padding: '16px 22px',
                        borderRadius: '14px',
                        backgroundColor: isPaid ? '#F0FDF4' : '#FFFBEB',
                        border: `1px solid ${isPaid ? '#BBF7D0' : '#FDE68A'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '12px',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <div style={{
                                width: '42px',
                                height: '42px',
                                borderRadius: '10px',
                                backgroundColor: isPaid ? '#DCFCE7' : '#FEF3C7',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '20px'
                            }}>
                                💳
                            </div>
                            <div>
                                <p style={{ margin: 0, fontWeight: 700, fontSize: '14.5px', color: '#1E293B', fontFamily: FONTS_FAMILY.bold.body }}>
                                    Estado del Pago: <span style={{ color: isPaid ? '#15803D' : '#B45309' }}>{paymentInfo.payment_status || 'PENDIENTE'}</span>
                                </p>
                                <p style={{ margin: '3px 0 0 0', fontSize: '12.5px', color: '#64748B', fontFamily: FONTS_FAMILY.regular.body }}>
                                    Método: <strong>{paymentInfo.payment_method || 'N/A'}</strong> • Monto: <strong>RD$ {paymentInfo.payment_amount || '0.00'}</strong>
                                </p>
                            </div>
                        </div>

                        {paymentInfo.succesfullyPayment_date && (
                            <span style={{ fontSize: '12px', color: '#64748B', fontWeight: 600, fontFamily: FONTS_FAMILY.regular.body }}>
                                Pagado el: {paymentInfo.succesfullyPayment_date}
                            </span>
                        )}
                    </div>
                );
            })()}

            {/* Action Required Notifications */}
            {rolBase !== ROLE_TYPE.COMPANY &&
                docIdentification === citizenID &&
                statusID != 10 && statusID != 8 && statusID != 7 &&
                serviceRequestedDetail?.requiredActions?.map((item, idx) => {
                    const isCompleted = item?.status === true || item?.status === 1 || item?.status === 'true' || item?.completed === true;

                    if (isCompleted) {
                        return null;
                    }

                    const comments = serviceRequestedDetail?.request?.comments ? [...serviceRequestedDetail.request.comments] : [];
                    comments.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
                    const lastComment = comments.length > 0 ? comments[comments.length - 1] : null;

                    // Si el último comentario por fecha de creación (created_at) tiene user_id === null o undefined, NO mostrar la barra
                    if (lastComment && (lastComment.user_id === null || lastComment.user_id === undefined)) {
                        return null;
                    }

                    let rawText = (lastComment && lastComment.user_id !== null && lastComment.user_id !== undefined)
                        ? lastComment.text
                        : (item.message || '');

                    let cleanMessage = rawText || '';
                    try {
                        if (cleanMessage.includes('Â') || cleanMessage.includes('Ã')) {
                            cleanMessage = decodeURIComponent(escape(cleanMessage));
                        }
                    } catch (e) {}

                    return (
                        <Fragment key={idx}>
                            <div style={{ marginBottom: '12px' }}>
                                <DeskNotification
                                    variant={'warning'}
                                    isSelected={selectedActionRequired?.id === item?.id && serviceRequestedDetail?.requiredActions}
                                    disableCloseButton={true}
                                    showButton={serviceRequestedDetail?.request?.status?.base_status_id == 1}
                                    buttonTitle="Completar"
                                    onButtonClick={() => handleCompleteActionRequired(item)}
                                    message={cleanMessage}
                                />
                            </div>
                        </Fragment>
                    );
                })}

            {/* Rating Modal */}
            {rolBase !== ROLE_TYPE.COMPANY && serviceRequestedDetail?.request?.solution && serviceRequestedDetail.request.rating?.length <= 0 && (
                <FormModal
                    open={ratingModalIsOpen}
                    onClose={handleRatingModalVisibility}
                    title=""
                    maxWidth="sm"
                >
                            {/* Custom Modern Header */}
                            <div className="rating-modal-header">
                                <div className="rating-modal-title-row">
                                    <div className="rating-modal-icon-container">
                                        <StarRoundedIcon sx={{ fontSize: '28px', color: '#B45309' }} />
                                    </div>
                                    <div>
                                        <h3 className="rating-modal-title">
                                            Calificación de Servicio
                                        </h3>
                                        <p className="rating-modal-subtitle">
                                            Agradecemos su opinión. Por favor, califique su experiencia con este trámite.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Form fields */}
                            <div className="rating-modal-body">
                                <div className="rating-stars-wrapper">
                                    <span className="rating-stars-label">¿Cómo calificaría la atención recibida?</span>
                                    <Rating
                                        onChange={(e, newValue) => setRating(newValue)}
                                        value={rating}
                                        precision={0.5}
                                        size="large"
                                        sx={{
                                            fontSize: '36px',
                                            '& .MuiRating-iconFilled': {
                                                color: '#F59E0B'
                                            },
                                            '& .MuiRating-iconHover': {
                                                color: '#D97706'
                                            }
                                        }}
                                    />
                                </div>

                                <TextField
                                    id="comment"
                                    title="Comentarios u Observaciones"
                                    placeholder="Cuéntenos más sobre su experiencia (opcional)..."
                                    value={formik.values.comment}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.comment && Boolean(formik.errors.comment)}
                                    helperText={formik.touched.comment && formik.errors.comment}
                                    multiline
                                    minRows={4}
                                />
                            </div>

                            {/* Modern Footer Actions */}
                            <div className="rating-modal-footer">
                                <button
                                    type="button"
                                    className="rating-modal-cancel-btn"
                                    onClick={handleRatingModalVisibility}
                                >
                                    CANCELAR
                                </button>
                                <button
                                    type="button"
                                    className="rating-modal-submit-btn"
                                    onClick={formik.handleSubmit}
                                >
                                    ENVIAR CALIFICACIÓN
                                </button>
                            </div>
                        </FormModal>
                    )}

            {/* Modern Tab Navigation */}
            <div style={{
                display: 'flex',
                gap: '4px',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '24px',
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
            }}>
                {tabItems.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => handleChangeMenu(tab.id)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '12px 20px',
                            border: 'none',
                            borderBottom: activeMenu === tab.id ? `3px solid ${COLORS.secondary}` : '3px solid transparent',
                            background: activeMenu === tab.id ? 'rgba(32, 42, 93, 0.05)' : 'transparent',
                            color: activeMenu === tab.id ? COLORS.secondary : '#94a3b8',
                            fontFamily: FONTS_FAMILY.bold.body,
                            fontSize: '14px',
                            fontWeight: activeMenu === tab.id ? 700 : 500,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s ease',
                            borderRadius: '8px 8px 0 0',
                            marginBottom: '-2px'
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div ref={actionRequiredRef}>
                {activeMenu === MenuOptions.details ? (
                    <Details />
                ) : activeMenu === MenuOptions.claims ? (
                    <ComplaintsAndClaims
                        companyID={companyID}
                        companyName={companyName}
                    />
                ) : activeMenu === MenuOptions.payment ? (
                    <Payment />
                ) : activeMenu === MenuOptions.messages ? (
                    <Messages />
                ) : (
                    activeMenu === MenuOptions.actionRequired && (
                        <ActionsRequired
                            finishActionRequired={finishActionRequired}
                            data={selectedActionRequired}
                        />
                    )
                )}
            </div>
        </div>
    );
}

export default ServiceRequestedDetails;
